import React from "react";
import { Inbox, ExternalLink } from "lucide-react";
import { t } from "@/lib/i18n";

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const STREAM_CLASS = {
  amazon: "text-[#f3c669] border-[#f3c669]/40 bg-[#f3c669]/10",
  marketplace: "text-[#c084fc] border-[#c084fc]/30 bg-[#c084fc]/10",
  referral: "text-[#60a5fa] border-[#60a5fa]/30 bg-[#60a5fa]/10",
};

export default function PayoutsTable({ rows, lang, showScout = false }) {
  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-3"
      data-testid="payouts-table-section"
    >
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "payouts_table")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{t(lang, "payouts_table_sub")}</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[#1e3a61] rounded-xl py-10 text-center bg-[#0f2038]/40">
          <div className="w-11 h-11 mx-auto rounded-full grid place-items-center bg-[#132744] border border-[#1e3a61]">
            <Inbox className="w-5 h-5 text-slate-400" />
          </div>
          <p className="mt-3 font-semibold text-slate-200 text-sm">
            {t(lang, "empty_payouts")}
          </p>
          <p className="text-xs text-slate-500 mt-1">{t(lang, "empty_payouts_sub")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="payouts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-[#1e3a61]">
                <th className="py-3 pr-4">{t(lang, "col_created")}</th>
                {showScout && <th className="py-3 pr-4">{t(lang, "col_scout")}</th>}
                <th className="py-3 pr-4">{t(lang, "col_stream")}</th>
                <th className="py-3 pr-4">{t(lang, "col_source")}</th>
                <th className="py-3 pr-4 hidden md:table-cell">{t(lang, "col_ref")}</th>
                <th className="py-3 pr-4 text-right">{t(lang, "col_amount")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[#1e3a61]/50 hover:bg-[#1a3254]/40 transition-colors"
                  data-testid={`payout-row-${r.id}`}
                >
                  <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">
                    {fmtDate(r.occurred_at, lang)}
                  </td>
                  {showScout && (
                    <td className="py-3 pr-4 text-slate-200">{r.scout_name}</td>
                  )}
                  <td className="py-3 pr-4">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                        STREAM_CLASS[r.stream] || STREAM_CLASS.referral
                      }`}
                    >
                      {r.stream}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-200">{r.source}</td>
                  <td className="py-3 pr-4 hidden md:table-cell text-slate-400 font-mono text-xs">
                    {r.external_ref ? (
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {r.external_ref}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right font-mono tabular-nums font-semibold text-[#f3c669]">
                    {fmtUSD(r.amount_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
